/*
 * The Flaire — Private Concierge Search + Chat engine.
 *
 * A deterministic, rules-based conversational concierge: it parses shopper intent
 * (recipient, budget, category, material, style) out of free text, scores every real,
 * live product from the server-rendered catalog against that intent, and carries a
 * running back-and-forth conversation — including follow-up questions about a specific
 * product's materials/design (grounded in that product's own real description text),
 * brand FAQ questions (grounded in the store's real, published FAQ answers), and
 * "add it to my bag" chat commands. No invented prices, no invented stock claims, no
 * invented facts — every answer traces back to real catalog data or the real FAQ page.
 * It is NOT a generative-AI / LLM backend (deliberate choice — see delivery notes).
 */
(function () {
  'use strict';

  var root = document.getElementById('FlaireConcierge');
  if (!root) return;

  var CATALOG = [];
  var BESTSELLERS = [];
  try { CATALOG = JSON.parse(document.getElementById('fr-cc-catalog').textContent || '[]'); } catch (e) { CATALOG = []; }
  try { BESTSELLERS = JSON.parse(document.getElementById('fr-cc-bestsellers').textContent || '[]'); } catch (e) { BESTSELLERS = []; }

  var els = {
    form: document.getElementById('fr-cc-form'),
    input: document.getElementById('fr-cc-query'),
    prompts: document.getElementById('fr-cc-prompts'),
    thread: document.getElementById('fr-cc-thread'),
    results: document.getElementById('fr-cc-results'),
    resultsNote: document.getElementById('fr-cc-results-note'),
    grid: document.getElementById('fr-cc-grid'),
    clear: document.getElementById('fr-cc-clear'),
    crosssell: document.getElementById('fr-cc-crosssell'),
    crosssellRow: document.getElementById('fr-cc-crosssell-row'),
    empty: document.getElementById('fr-cc-empty'),
    emptyMsg: document.getElementById('fr-cc-empty-msg'),
    emptyActions: document.getElementById('fr-cc-empty-actions'),
    bestsellerGrid: document.getElementById('fr-cc-bestseller-grid'),
    hero: document.querySelector('.fr-cc-hero'),
    toast: document.getElementById('fr-cc-toast'),
    composer: document.getElementById('fr-cc-composer'),
    composerForm: document.getElementById('fr-cc-composer-form'),
    composerInput: document.getElementById('fr-cc-composer-input'),
  };

  // ---------- generic helpers ----------

  function money(cents) {
    if (cents == null) return '';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function normalize(s) {
    return (s || '').toString().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '').toLowerCase();
  }

  function joinList(arr) {
    if (!arr.length) return '';
    if (arr.length === 1) return arr[0];
    return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
  }

  function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

  // ---------- query parsing (recipient / budget / category / material / style) ----------

  var MATERIAL_TAGS = { 'rose gold': '18k-rose-gold', 'white gold': '18k-white-gold', gold: '18k-gold' };
  var CATEGORY_MAP = {
    ring: 'ring', rings: 'ring',
    chain: 'chain', chains: 'chain',
    necklace: 'necklace', necklaces: 'necklace', pendant: 'necklace', pendants: 'necklace',
    bracelet: 'bracelet', bracelets: 'bracelet',
    earring: 'earrings', earrings: 'earrings', hoop: 'earrings', hoops: 'earrings', stud: 'earrings', studs: 'earrings',
    anklet: 'anklet', anklets: 'anklet',
    set: 'set', sets: 'set',
  };
  var STYLE_TAGS = ['cuban-link', 'figaro', 'tennis', 'hoops', 'statement-ring', 'statement-necklace',
    'signet-ring', 'iced-out', 'pearl', 'engraved', 'drop-earrings', 'rope-chain', 'mariner-chain',
    'franco-chain', 'cross-pendant'];
  var MEN_WORDS = ['husband', 'boyfriend', 'dad', 'father', 'brother', 'son', 'groom', 'groomsman', 'groomsmen', 'him', 'his', 'for him', 'men', "men's", 'guy'];
  var WOMEN_WORDS = ['wife', 'girlfriend', 'mom', 'mother', 'sister', 'daughter', 'bride', 'bridesmaid', 'bridesmaids', 'her', 'hers', 'for her', 'women', "women's", 'ladies', 'lady'];

  function parseQuery(raw) {
    var q = (raw || '').toLowerCase().trim();
    var parsed = { budgetMin: null, budgetMax: null, category: null, material: null, materialFamily: null, recipient: null, styleTags: [], tokens: [] };

    var mUnder = q.match(/(?:under|below|less than)\s*\$?\s*(\d+)/);
    if (mUnder) parsed.budgetMax = parseInt(mUnder[1], 10);

    var mSpend = q.match(/\$?\s*(\d+)\s*(?:to spend|budget|to build)/);
    if (mSpend && parsed.budgetMax == null) parsed.budgetMax = parseInt(mSpend[1], 10);

    var mBetween = q.match(/(?:between\s*)?\$?(\d+)\s*(?:-|to|and)\s*\$?(\d+)/);
    if (mBetween) { parsed.budgetMin = parseInt(mBetween[1], 10); parsed.budgetMax = parseInt(mBetween[2], 10); }

    if (q.indexOf('rose gold') > -1) { parsed.material = MATERIAL_TAGS['rose gold']; }
    else if (q.indexOf('white gold') > -1) { parsed.material = MATERIAL_TAGS['white gold']; }
    else if (q.indexOf('silver') > -1) { parsed.materialFamily = 'silverish'; }
    else if (q.indexOf('gold') > -1) { parsed.material = MATERIAL_TAGS['gold']; }

    Object.keys(CATEGORY_MAP).forEach(function (word) {
      if (parsed.category) return;
      if (new RegExp('\\b' + word + '\\b').test(q)) parsed.category = CATEGORY_MAP[word];
    });

    STYLE_TAGS.forEach(function (tag) {
      var word = tag.replace(/-/g, ' ');
      if (q.indexOf(word) > -1) parsed.styleTags.push(tag);
    });

    if (MEN_WORDS.some(function (w) { return q.indexOf(w) > -1; })) parsed.recipient = 'men';
    if (WOMEN_WORDS.some(function (w) { return q.indexOf(w) > -1; })) parsed.recipient = parsed.recipient ? parsed.recipient : 'women';
    if (q.indexOf('myself') > -1 || q.indexOf('for me') > -1 || q.indexOf('my own') > -1) parsed.recipient = 'self';

    parsed.tokens = q.replace(/[^\w\s$]/g, ' ').split(/\s+/).filter(function (t) { return t.length > 2; });

    return parsed;
  }

  function scoreProduct(p, parsed) {
    var score = 0;
    var titleLower = p.title.toLowerCase();
    var tags = (p.tags || []).map(function (t) { return t.toLowerCase(); });
    var type = (p.type || '').toLowerCase();

    if (parsed.category) {
      if (type === parsed.category) score += 3; else score -= 1;
    }
    if (parsed.material && tags.indexOf(parsed.material) > -1) score += 2;
    if (parsed.materialFamily === 'silverish' && (tags.indexOf('silver') > -1 || tags.indexOf('18k-white-gold') > -1)) score += 2;

    if (parsed.recipient === 'men') {
      if (tags.indexOf('ladies-jewellery') > -1 && tags.indexOf('mens-jewellery') === -1) return -Infinity;
      if (tags.indexOf('mens-jewellery') > -1) score += 2;
    }
    if (parsed.recipient === 'women') {
      if (tags.indexOf('mens-jewellery') > -1 && tags.indexOf('ladies-jewellery') === -1) return -Infinity;
      if (tags.indexOf('ladies-jewellery') > -1) score += 2;
    }

    (parsed.styleTags || []).forEach(function (st) { if (tags.indexOf(st) > -1) score += 2; });
    (parsed.tokens || []).forEach(function (tok) { if (titleLower.indexOf(tok) > -1) score += 1; });

    if (parsed.budgetMax != null) {
      if (p.price > parsed.budgetMax * 100) return -Infinity;
      score += 2;
    }
    if (parsed.budgetMin != null && p.price < parsed.budgetMin * 100) return -Infinity;

    if (!p.available) score -= 10;

    return score;
  }

  function buildResponseSentence(ctxLocal, count) {
    var bits = [];
    if (ctxLocal.recipient === 'men') bits.push('for him');
    else if (ctxLocal.recipient === 'women') bits.push('for her');
    else if (ctxLocal.recipient === 'self') bits.push('for you');
    if (ctxLocal.category) bits.push(ctxLocal.category + (count === 1 ? '' : 's'));
    var subject = bits.length ? bits.join(' ') : 'pieces';
    var budget = '';
    if (ctxLocal.budgetMax != null && ctxLocal.budgetMin != null) budget = ' between $' + ctxLocal.budgetMin + ' and $' + ctxLocal.budgetMax;
    else if (ctxLocal.budgetMax != null) budget = ' under $' + ctxLocal.budgetMax;
    if (count === 0) {
      return "I don't have an exact match" + budget + " right now, so I've pulled the closest alternatives and what's selling well instead.";
    }
    return 'I found <b>' + count + '</b> ' + subject + budget + " that I'd recommend.";
  }

  // ---------- product knowledge: materials/design, grounded in each product's own copy ----------

  var MATERIAL_RULES = [
    { label: '316L surgical-grade stainless steel', re: /316l[\w\s-]*steel/i },
    { label: 'a high-quality brass base', re: /brass/i },
    { label: 'triple 18K gold plating', re: /triple[\s-](?:18k gold plating|coated in 18k gold|plated|gold plating)/i },
    { label: '18K rose gold plating', re: /18k rose gold/i },
    { label: '18K white gold plating', re: /18k white gold/i },
    { label: 'rhodium plating', re: /rhodium/i },
    { label: '18K gold plating', re: /18k gold/i },
    { label: '925 sterling silver', re: /925 sterling silver/i },
    { label: 'sterling silver', re: /\bsterling silver\b/i },
    { label: 'hand-set 5A cubic zirconia stones', re: /5a (?:cubic zirconia|cz)/i },
    { label: 'AAA-grade cubic zirconia stones', re: /aaa-grade/i },
    { label: 'cubic zirconia stones', re: /cubic zirconia|\bcz\b/i },
    { label: 'hypoallergenic', re: /hypoallergenic/i },
    { label: 'tarnish-resistant', re: /tarnish[\s-](?:resistant|proof)/i },
    { label: 'waterproof', re: /waterproof/i },
    { label: 'sweat-proof', re: /sweat[\s-]?proof/i },
    { label: 'heat-proof', re: /heat[\s-]?proof/i },
  ];

  function extractMaterialFacts(desc) {
    var out = [];
    MATERIAL_RULES.forEach(function (rule) {
      if (out.length >= 6) return;
      if (rule.re.test(desc || '')) out.push(rule.label);
    });
    return out;
  }

  function materialsFromTags(tags) {
    tags = (tags || []).map(function (t) { return t.toLowerCase(); });
    var out = [];
    if (tags.indexOf('18k-rose-gold') > -1) out.push('18K rose gold plating over stainless steel');
    else if (tags.indexOf('18k-white-gold') > -1) out.push('rhodium / 18K white gold plating over stainless steel');
    else if (tags.indexOf('18k-gold') > -1) out.push('18K gold plating over stainless steel');
    if (tags.indexOf('cubic-zirconia') > -1) out.push('hand-set cubic zirconia stones');
    return out;
  }

  function firstSentences(desc, n, maxLen) {
    if (!desc) return '';
    var parts = desc.split('. ');
    var out = parts.slice(0, n).join('. ');
    if (n < parts.length && !/[.!?]$/.test(out)) out += '.';
    var featuresIdx = out.toLowerCase().indexOf('features');
    if (featuresIdx > 30) out = out.slice(0, featuresIdx).trim();
    if (out.length > maxLen) {
      out = out.slice(0, maxLen);
      var lastSpace = out.lastIndexOf(' ');
      if (lastSpace > 0) out = out.slice(0, lastSpace);
      out += '…';
    }
    return out;
  }

  // Closing nudges for a single-product detail answer — the moment someone asks about
  // one specific piece is the highest-intent moment in the conversation, so the reply
  // should always end by making it easy to say yes, not just stop at information.
  var DETAIL_CTAS = [
    "It's one of our most-loved pieces — say the word and I'll add it to your bag.",
    "This one tends to sell quickly — happy to add it to your bag right now if you're ready.",
    "I can add this to your bag whenever you are, or pull a couple of alternatives if you'd like to compare.",
  ];

  function buildDetailAnswer(p) {
    var desc = p.description || '';
    var facts = extractMaterialFacts(desc);
    if (!facts.length) facts = materialsFromTags(p.tags || []);
    var blurb = firstSentences(desc, 2, 240);

    var priceBit = money(p.price) + (p.compareAtPrice ? ' <span style="text-decoration:line-through;opacity:.6">' + money(p.compareAtPrice) + '</span>' : '');
    var stockBit = '';
    if (!p.available) stockBit = " It's sold out right now — I can show you something similar if you'd like.";
    else if (p.inventory != null && p.inventory > 0 && p.inventory <= 10) stockBit = ' Only ' + p.inventory + ' left at the moment.';

    var parts = [];
    parts.push('<b>' + escapeHtml(p.title) + '</b> — ' + priceBit + '.');
    if (blurb) parts.push(escapeHtml(blurb) + (/[.!?…]$/.test(blurb) ? '' : '.'));
    if (facts.length) parts.push("It's crafted with " + escapeHtml(joinList(facts)) + '.');
    if (stockBit) parts.push(stockBit);
    if (p.available) {
      parts.push(pick(DETAIL_CTAS));
      if (!ctx.offerMentioned) { parts.push(OFFER_LINE); ctx.offerMentioned = true; }
    }
    return parts.join(' ');
  }

  // ---------- store FAQ knowledge base (verbatim from the live, brand-approved /pages/faqs "Ask Aria" page) ----------

  var FAQ_KB = [
    { a: "Every piece is 18K gold-plated — or rhodium-plated for our silver finishes — over a core of 316L surgical stainless steel, the same grade used in medical implants. Many everyday pieces are triple-plated for extra longevity.", kw: ['made of', 'material', 'materials', 'what.{0,15}made', 'composition', 'what.{0,10}this (made|built)'] },
    { a: "No. Our pieces are waterproof, sweatproof and tarnish-resistant, and fully hypoallergenic — safe for sensitive skin. Wear them in the shower, at the gym, in the ocean.", kw: ['tarnish', 'fade', 'turn.{0,10}green', 'skin react', 'allerg', 'waterproof', 'sweat'] },
    { a: "It's real 18K gold plated over solid surgical steel — never hollow brass. That's what makes it durable and long-lasting, unlike the flash-plated costume jewellery you'll find elsewhere.", kw: ['real gold', 'is (it|this) gold', 'genuine gold', 'solid gold'] },
    { a: "Cuban link, Figaro, rope and wheat chains, bracelets, rings, pendants, iced-out cubic-zirconia pieces and matching sets — in both gold and silver finishes.", kw: ['what styles', 'what types? of jewel', 'what.{0,10}(do you|you) make', 'collections'] },
    { a: "Every product page lists exact lengths and sizes. If you're between sizes or unsure, email support@the-flaire.com and we'll help you choose the perfect fit.", kw: ['\\bsize\\b', 'sizing', '\\bfit\\b', 'ring size', 'how big', 'what length'] },
    { a: "Store it dry, keep it away from perfume, cologne and harsh chemicals, and buff it with the polishing cloth included in every order after heavy wear. A full care guide arrives in your box.", kw: ['care for', 'how.{0,10}clean', 'maintain', 'look after'] },
    { a: "Worldwide, with fast dispatch focused on the US and Australia. Every order is fully insured and trackable, door to door.", kw: ['where.{0,15}ship', 'ship to', 'deliver to', 'international'] },
    { a: "Standard is $9.95 (2–8 business days, complimentary over $99). Express is $14.95 (1–4 business days, complimentary over $150). Orders dispatch within 1–2 business days and you receive a tracking link the moment yours ships.", kw: ['shipping cost', 'how much.{0,15}ship', 'delivery time', 'how long.{0,15}(ship|take|arrive)', 'when will.{0,15}arrive', 'postage'] },
    { a: "In our signature matte-black magnetic box embossed in gold, with a velvet pouch or ring case, a polishing cloth and a care guide — gift-ready, every time.", kw: ['packaging', 'how.{0,10}arrive', 'gift box', 'come in\\b'] },
    { a: "Local duties or taxes may apply on international orders and are set by your country. Refused parcels are returned and credited, minus shipping.", kw: ['customs', 'duties', 'import tax'] },
    { a: "A 1-year warranty against manufacturing defects, backed by our 100% satisfaction guarantee.", kw: ['warranty', 'guarantee'] },
    { a: "Faulty items: 30-day returns with a prepaid label. Changed your mind or need a different size? We'll issue store credit valid for a year — you simply cover return shipping. Items must be unworn and in original condition and packaging.", kw: ['\\breturn', 'exchange', 'refund', 'send.{0,10}back'] },
    { a: "Accidental damage such as bending or dropping, chemical exposure (perfume, chlorine, lotions), and personalised, custom or personal-care items. Following the care guide keeps your warranty valid.", kw: ["isn'?t covered", 'not covered', 'excluded', "voids?.{0,10}warranty"] },
    { a: "All major cards, PayPal, and Afterpay — so you can split the cost over time, interest-free.", kw: ['payment', 'pay with', 'afterpay', 'klarna', 'paypal', 'credit card'] },
    { a: "Email support@the-flaire.com and a real person replies within 24 hours. We treat every message like a VIP.", kw: ['contact', 'reach you', 'talk to.{0,10}(a )?human', 'email you', 'customer service', '\\bsupport\\b'] },
  ];

  function matchFaq(lower) {
    var best = null, bestScore = 0;
    FAQ_KB.forEach(function (entry) {
      var score = 0;
      entry.kw.forEach(function (pattern) {
        try { if (new RegExp(pattern, 'i').test(lower)) score++; } catch (e) { /* noop */ }
      });
      if (score > bestScore) { bestScore = score; best = entry; }
    });
    return bestScore > 0 ? best : null;
  }

  function faqFollowups() {
    var arr = [];
    if (ctx.lastFocusProduct) arr.push('Back to the ' + ctx.lastFocusProduct.title);
    arr.push('Show me some recommendations');
    return arr;
  }

  // ---------- light advisory copy for the handful of "which is right for me" style prompts ----------

  var ADVISORY_KB = [
    { test: /gold or silver|which suits me|what suits me/i,
      a: "Both wear beautifully day to day — gold tends to read warmer against tan or olive skin tones, while our silver pieces (rhodium-plated) run cooler and suit almost everyone. It usually comes down to what's already in your jewelry box, but I can show you our top pieces in each so you can compare." },
    { test: /new to fine jewel|first piece|just starting|beginner/i,
      a: "For a first piece, I'd start with something versatile and easy to wear daily — a simple chain, a stackable ring, or a pair of hoops all work well with everything else you own." },
  ];

  function matchAdvisory(lower) {
    var hit = ADVISORY_KB.find ? ADVISORY_KB.find(function (e) { return e.test.test(lower); }) : null;
    if (!hit) {
      for (var i = 0; i < ADVISORY_KB.length; i++) { if (ADVISORY_KB[i].test.test(lower)) { hit = ADVISORY_KB[i]; break; } }
    }
    return hit ? hit.a : null;
  }

  // ---------- rendering: product cards / grids (unchanged visual design) ----------

  function cardHtml(p) {
    var lowStock = (p.inventory != null && p.inventory > 0 && p.inventory <= 10);
    var badge = '';
    if (!p.available) badge = '<span class="fr-cc-badge soldout">Sold out</span>';
    else if (lowStock) badge = '<span class="fr-cc-badge low">Only ' + p.inventory + ' left</span>';
    else if (p.compareAtPrice) badge = '<span class="fr-cc-badge">Save ' + money(p.compareAtPrice - p.price) + '</span>';

    var priceHtml = '<span class="now">' + money(p.price) + '</span>' +
      (p.compareAtPrice ? '<span class="was">' + money(p.compareAtPrice) + '</span>' : '');

    return (
      '<article class="fr-cc-card" data-id="' + p.id + '">' +
        '<a class="fr-cc-card-media" href="' + p.url + '">' +
          (p.image ? '<img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
          badge +
        '</a>' +
        '<div class="fr-cc-card-body">' +
          '<p class="fr-cc-card-type">' + escapeHtml(p.type || '') + '</p>' +
          '<h3 class="fr-cc-card-title">' + escapeHtml(p.title) + '</h3>' +
          '<div class="fr-cc-card-price">' + priceHtml + '</div>' +
          '<div class="fr-cc-card-actions">' +
            '<a class="fr-cc-btn" href="' + p.url + '">View</a>' +
            '<button type="button" class="fr-cc-btn primary fr-cc-add" data-variant-id="' + p.variantId + '"' + (p.available ? '' : ' disabled') + '>' +
              (p.available ? 'Add to Cart' : 'Sold Out') +
            '</button>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function renderGrid(el, products) {
    el.innerHTML = products.map(cardHtml).join('');
  }

  function findCrossSell(top, allScored) {
    if (!top) return [];
    var topTags = (top.tags || []);
    var seen = {};
    return allScored
      .map(function (x) { return x.p; })
      .filter(function (p) { if (seen[p.id]) return false; seen[p.id] = true; return true; })
      .filter(function (p) { return p.id !== top.id && p.type !== top.type; })
      .filter(function (p) { return (p.tags || []).some(function (t) { return topTags.indexOf(t) > -1; }); })
      .slice(0, 4);
  }

  // ---------- conversation memory ----------

  var ctx = null;
  function resetContext() {
    ctx = {
      recipient: null, budgetMin: null, budgetMax: null, category: null,
      material: null, materialFamily: null, styleTags: [],
      lastResults: [], lastFocusProduct: null,
      shownIds: [], offerMentioned: false,
    };
  }
  resetContext();

  function mergeContext(parsed, lowerRaw) {
    var isTopicSwitch = !!(parsed.category && ctx.category && parsed.category !== ctx.category);
    if (isTopicSwitch || /\b(instead|actually|different|something else entirely|forget that)\b/.test(lowerRaw)) {
      ctx.recipient = null; ctx.budgetMin = null; ctx.budgetMax = null; ctx.category = null;
      ctx.material = null; ctx.materialFamily = null; ctx.styleTags = [];
      ctx.shownIds = [];
    }
    if (parsed.recipient) ctx.recipient = parsed.recipient;
    if (parsed.category) ctx.category = parsed.category;
    if (parsed.material) { ctx.material = parsed.material; ctx.materialFamily = null; }
    if (parsed.materialFamily) { ctx.materialFamily = parsed.materialFamily; ctx.material = null; }
    if (parsed.budgetMax != null) ctx.budgetMax = parsed.budgetMax;
    if (parsed.budgetMin != null) ctx.budgetMin = parsed.budgetMin;
    if (/\b(any budget|no budget|any price|no limit|remove.{0,10}budget)\b/.test(lowerRaw)) { ctx.budgetMax = null; ctx.budgetMin = null; }
    (parsed.styleTags || []).forEach(function (t) { if (ctx.styleTags.indexOf(t) === -1) ctx.styleTags.push(t); });
  }

  // ---------- resolving "the Nero Ring" / "the first one" / "it" / "the bracelet" against real products ----------

  function findProductByName(lower, list) {
    var t = normalize(lower);
    var exact = list.filter(function (p) { return t.indexOf(normalize(p.title)) > -1; });
    if (exact.length) return exact;
    return list.filter(function (p) {
      var fw = normalize(p.title).split(' ')[0];
      if (fw.length < 4) return false;
      return new RegExp('\\b' + fw + '\\b').test(t);
    });
  }

  var REF_TRIGGER = /(tell me more|more (details|info)|what'?s (it|that|this)|made of|material|how much (is|does)|does it come|is it available|in stock|the first one|the second one|the third one|that one|this one)/;

  function resolveProductReference(lower) {
    var named = findProductByName(lower, CATALOG);

    var ordMatch = lower.match(/\b(first|1st|second|2nd|third|3rd|fourth|4th)\b/);
    if (ordMatch && ctx.lastResults.length) {
      var map = { first: 0, '1st': 0, second: 1, '2nd': 1, third: 2, '3rd': 2, fourth: 3, '4th': 3 };
      var idx = map[ordMatch[1]];
      if (ctx.lastResults[idx]) return { type: 'single', product: ctx.lastResults[idx] };
    }

    if (named.length === 1) return { type: 'single', product: named[0] };
    if (named.length > 1) {
      var inLast = named.filter(function (p) { return ctx.lastResults.some(function (r) { return r.id === p.id; }); });
      if (inLast.length === 1) return { type: 'single', product: inLast[0] };
      return { type: 'ambiguous', candidates: named };
    }

    if (/^(it|that|this|that one|this one)\b/.test(lower) && ctx.lastFocusProduct) {
      return { type: 'single', product: ctx.lastFocusProduct };
    }

    var catWord = null;
    Object.keys(CATEGORY_MAP).some(function (w) {
      if (new RegExp('\\bthe\\s+' + w + '\\b').test(lower)) { catWord = w; return true; }
      return false;
    });
    if (catWord && ctx.lastResults.length) {
      var cat = CATEGORY_MAP[catWord];
      var inCat = ctx.lastResults.filter(function (p) { return (p.type || '').toLowerCase() === cat; });
      if (inCat.length === 1) return { type: 'single', product: inCat[0] };
      if (inCat.length > 1) return { type: 'ambiguous', candidates: inCat };
    }

    if (REF_TRIGGER.test(lower) && ctx.lastFocusProduct) {
      return { type: 'single', product: ctx.lastFocusProduct };
    }

    return { type: 'none' };
  }

  // ---------- cart ----------

  function postAddToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    }).then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); });
  }

  function addToCart(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId || variantId === 'null') return;
    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = 'Adding…';
    postAddToCart(variantId)
      .then(function (result) {
        if (result.ok) {
          btn.textContent = 'Added ✓';
          toast('Added to your bag.');
          updateCartCount();
          setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1800);
        } else {
          btn.textContent = original;
          btn.disabled = false;
          toast(result.data && result.data.description ? result.data.description : "Couldn't add that — try the product page.");
        }
      })
      .catch(function () {
        btn.textContent = original;
        btn.disabled = false;
        toast('Something went wrong — try the product page.');
      });
  }

  function doAddToCart(product) {
    if (!product || !product.variantId || product.variantId === 'null' || !product.available) {
      appendConciergeReply("That one's sold out right now, so I can't add it — want to see something similar instead?", ['Show me similar pieces']);
      return;
    }
    postAddToCart(product.variantId)
      .then(function (result) {
        if (result.ok) {
          toast('Added to your bag.');
          updateCartCount();
          // The moment right after a cart add is the best possible time to mention the
          // real, live Buy 2 Get 1 Free offer — it's a natural upsell, not a hard sell.
          var offerNudge = !ctx.offerMentioned;
          if (offerNudge) ctx.offerMentioned = true;
          var addedMsg = 'Added the <b>' + escapeHtml(product.title) + '</b> to your bag ✓ — ' + money(product.price) + '. ' +
            (offerNudge
              ? "You're also covered by Buy 2, Get 1 Free right now — want me to find a second piece to pair with it?"
              : "Want me to keep browsing with you, or is there anything else you'd like to know?");
          appendConciergeReply(addedMsg, offerNudge ? ['Find a second piece', 'Keep browsing'] : ['Keep browsing', 'Something else']);
        } else {
          appendConciergeReply("I couldn't add that one — try the product page directly, or tell me if you'd like an alternative.", []);
        }
      })
      .catch(function () {
        appendConciergeReply('Something went wrong adding that — try the product page directly.', []);
      });
  }

  function updateCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (bubble) bubble.textContent = cart.item_count;
      })
      .catch(function () {});
  }

  function toast(msg) {
    els.toast.textContent = msg;
    els.toast.hidden = false;
    requestAnimationFrame(function () { els.toast.classList.add('show'); });
    clearTimeout(toast._t);
    toast._t = setTimeout(function () {
      els.toast.classList.remove('show');
      setTimeout(function () { els.toast.hidden = true; }, 300);
    }, 2600);
  }

  // ---------- thread rendering (appends turns; never overwrites conversation history) ----------

  function appendUserTurn(text) {
    els.thread.hidden = false;
    var row = document.createElement('div');
    row.className = 'fr-cc-bubble-row user';
    row.innerHTML = '<div class="fr-cc-bubble fr-cc-bubble--user">' + escapeHtml(text) + '</div>';
    els.thread.appendChild(row);
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return row;
  }

  function appendTypingIndicator() {
    var row = document.createElement('div');
    row.className = 'fr-cc-bubble-row bot';
    row.innerHTML = '<div class="fr-cc-typing"><span></span><span></span><span></span></div>';
    els.thread.appendChild(row);
    return row;
  }

  function appendConciergeReply(html, followups) {
    var row = document.createElement('div');
    row.className = 'fr-cc-bubble-row bot';
    row.innerHTML = '<div class="fr-cc-bubble">' + html + '</div>';
    els.thread.appendChild(row);
    if (followups && followups.length) {
      var chipsWrap = document.createElement('div');
      chipsWrap.className = 'fr-cc-followups';
      chipsWrap.innerHTML = followups.slice(0, 4).map(function (q) {
        return '<button type="button" class="fr-cc-chip" data-q="' + escapeHtml(q) + '">' + escapeHtml(q) + '</button>';
      }).join('');
      els.thread.appendChild(chipsWrap);
      chipsWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ---------- search-turn reply copy ----------

  var RESULT_INTROS = ["Here's what I'd recommend for you", "I'd start with these", "Here's what I found for you", "These caught my eye for you"];

  // Varied closing questions that nudge toward a decision instead of a flat re-ask —
  // part of the "conversion specialist" behavior: every reply should move the
  // conversation toward a next step (details or bag), not just dead-end.
  var CLOSING_QUESTIONS = [
    'Want the full details on one, or should I keep narrowing it down?',
    "Happy to go deeper on any of these — or if one's already speaking to you, I can add it to your bag right now.",
    'Want me to tell you more about one, or add a favorite straight to your bag?',
    'Want details on any of them, or should I keep looking?',
  ];

  // Mentioned at most once per conversation — real, live offer, not invented.
  var OFFER_LINE = 'Everything you see also qualifies for Buy 2, Get 1 Free, so a second piece works out free.';

  function buildChatReply(ctxLocal, resultsList, usedBestsellers) {
    var offerBit = '';
    if (!ctx.offerMentioned && resultsList.length) {
      offerBit = ' ' + OFFER_LINE;
      ctx.offerMentioned = true;
    }
    if (usedBestsellers) {
      var pair = resultsList.slice(0, 2).map(function (p) { return '<b>' + escapeHtml(p.title) + '</b> at ' + money(p.price); }).join(' and ');
      return "Here's what's selling best with our customers right now — " + pair + ". Take a look below, or tell me more about what you're after and I'll narrow it down." + offerBit;
    }
    var bits = [];
    if (ctxLocal.recipient === 'men') bits.push('for him');
    else if (ctxLocal.recipient === 'women') bits.push('for her');
    else if (ctxLocal.recipient === 'self') bits.push('for you');
    if (ctxLocal.category) bits.push(ctxLocal.category + (resultsList.length === 1 ? '' : 's'));
    var subject = bits.length ? bits.join(' ') : 'pieces';
    var budget = '';
    if (ctxLocal.budgetMax != null && ctxLocal.budgetMin != null) budget = ' between $' + ctxLocal.budgetMin + ' and $' + ctxLocal.budgetMax;
    else if (ctxLocal.budgetMax != null) budget = ' under $' + ctxLocal.budgetMax;

    var top2 = resultsList.slice(0, 2);
    var namedBit = '';
    if (top2.length) {
      namedBit = ' — the <b>' + escapeHtml(top2[0].title) + '</b> at ' + money(top2[0].price);
      if (top2[1]) namedBit += ', and the <b>' + escapeHtml(top2[1].title) + '</b> at ' + money(top2[1].price);
      if (resultsList.length > 2) namedBit += ', plus ' + (resultsList.length - 2) + ' more below';
    }
    return pick(RESULT_INTROS) + ' — ' + resultsList.length + ' ' + subject + budget + namedBit + '. ' + pick(CLOSING_QUESTIONS) + offerBit;
  }

  function buildSearchFollowups(ctxLocal, resultsList) {
    var arr = [];
    if (resultsList[0]) arr.push('Tell me more about the ' + resultsList[0].title);
    if (ctxLocal.budgetMax == null) arr.push('Keep it under $100');
    if (!ctxLocal.category) arr.push('Just show me rings');
    arr.push('Something else');
    return arr.slice(0, 4);
  }

  // ---------- turn handlers ----------

  function handleDetailTurn(product) {
    ctx.lastFocusProduct = product;
    var html = buildDetailAnswer(product);
    var sameType = CATALOG.filter(function (p) { return p.id !== product.id && p.type === product.type && p.available; }).slice(0, 4);

    var followups = [];
    if (product.available) followups.push('Add it to my bag');
    if (sameType.length) followups.push('Show me similar pieces');
    followups.push('Something else');

    appendConciergeReply(html, followups);

    renderGrid(els.grid, [product]);
    els.results.hidden = false;
    els.empty.hidden = true;
    els.resultsNote.innerHTML = 'A closer look';
    if (sameType.length) { els.crosssell.hidden = false; renderGrid(els.crosssellRow, sameType); }
    else { els.crosssell.hidden = true; }
  }

  function handleSearchTurn(query) {
    var parsedNew = parseQuery(query);
    var lower = query.toLowerCase();
    mergeContext(parsedNew, lower);

    var isBestsellerAsk = /best.?sell|bestseller|trending|popular|top pick/i.test(query);
    var usedBestsellers = false;
    var resultsList;

    if (isBestsellerAsk) {
      resultsList = BESTSELLERS.slice(0, 8);
      usedBestsellers = true;
    } else {
      var scoreCtx = { recipient: ctx.recipient, category: ctx.category, material: ctx.material, materialFamily: ctx.materialFamily, styleTags: ctx.styleTags, budgetMax: ctx.budgetMax, budgetMin: ctx.budgetMin, tokens: parsedNew.tokens };
      var scored = CATALOG
        .map(function (p) { return { p: p, s: scoreProduct(p, scoreCtx) }; })
        .filter(function (x) { return x.s > -Infinity; })
        .sort(function (a, b) { return b.s - a.s; });

      var hasSignal = !!(ctx.category || ctx.material || ctx.materialFamily || ctx.recipient || ctx.budgetMax != null || ctx.budgetMin != null || ctx.styleTags.length);
      var hasPositiveMatch = scored.length && scored[0].s > 0;

      if (!hasSignal && !hasPositiveMatch) {
        resultsList = BESTSELLERS.slice(0, 8);
        usedBestsellers = true;
      } else {
        // Prefer pieces not already shown this conversation — so "something else" /
        // "keep looking" genuinely surfaces something else instead of repeating the
        // same list. Only fall back to repeats if there isn't enough unseen inventory
        // left to fill a decent shortlist.
        var unseen = scored.filter(function (x) { return ctx.shownIds.indexOf(x.p.id) === -1; });
        var pool = unseen.length >= 4 ? unseen : scored;
        resultsList = pool.slice(0, 8).map(function (x) { return x.p; });
      }
    }

    if (!resultsList.length) {
      appendConciergeReply("I couldn't find an exact match for that, so here's what's popular right now instead — tell me a bit more about what you're after and I'll narrow it down.", ['Everyday stack under $100', 'Statement ring for a night out', "This week's best seller"]);
      els.results.hidden = true;
      els.empty.hidden = false;
      els.emptyMsg.textContent = "Nothing matches that exactly — but let's not leave you empty-handed.";
      els.emptyActions.innerHTML = '';
      renderGrid(els.bestsellerGrid, BESTSELLERS);
      return;
    }

    ctx.lastResults = resultsList;
    ctx.lastFocusProduct = resultsList[0];
    resultsList.forEach(function (p) { if (ctx.shownIds.indexOf(p.id) === -1) ctx.shownIds.push(p.id); });

    var advisory = matchAdvisory(lower);
    var chatReply = (advisory ? advisory + ' ' : '') + buildChatReply(ctx, resultsList, usedBestsellers);
    appendConciergeReply(chatReply, buildSearchFollowups(ctx, resultsList));

    els.results.hidden = false;
    els.empty.hidden = true;
    els.resultsNote.innerHTML = usedBestsellers ? "This week's best sellers" : buildResponseSentence(ctx, resultsList.length);
    renderGrid(els.grid, resultsList);

    var crossSell = findCrossSell(resultsList[0], resultsList.map(function (p) { return { p: p }; }));
    if (crossSell.length) { els.crosssell.hidden = false; renderGrid(els.crosssellRow, crossSell); }
    else { els.crosssell.hidden = true; }
  }

  function handleTurn(query) {
    var lower = query.toLowerCase().trim();

    if (/^(start over|new search|never ?mind|restart|reset|forget (it|that))\b/.test(lower)) {
      resetContext();
      appendConciergeReply('No problem — clean slate. Who are you shopping for, or what are you after?', []);
      els.results.hidden = true;
      els.empty.hidden = true;
      return;
    }

    var wordCount = lower.split(/\s+/).filter(Boolean).length;
    if (wordCount <= 4) {
      if (/^(hi|hello|hey|yo|hiya|good morning|good afternoon|good evening)\b/.test(lower)) {
        appendConciergeReply("Hi there! Tell me who you're shopping for, a budget, or a style — or ask me anything about materials, sizing, shipping, or returns.", []);
        return;
      }
      if (/^(thanks|thank you|thx|ty|cheers|great|awesome|perfect|cool|nice)\b/.test(lower)) {
        appendConciergeReply("You're very welcome! Want me to keep looking, or is there anything else I can tell you?", ['Show me something else']);
        return;
      }
    }

    if (/\badd\b[^.?!]*\b(bag|cart)\b|\b(buy|purchase)\b (it|that|this|the one)\b/.test(lower)) {
      var addRef = resolveProductReference(lower);
      var addProduct = addRef.type === 'single' ? addRef.product : ctx.lastFocusProduct;
      if (addProduct) { doAddToCart(addProduct); return; }
    }

    var refResult = resolveProductReference(lower);
    if (refResult.type === 'single') { handleDetailTurn(refResult.product); return; }
    if (refResult.type === 'ambiguous') {
      var names = refResult.candidates.slice(0, 4).map(function (p) { return p.title; });
      appendConciergeReply("We've got a few of those — " + escapeHtml(names.join(', ')) + '. Which one did you mean?', names);
      return;
    }

    var faq = matchFaq(lower);
    if (faq) { appendConciergeReply(escapeHtml(faq.a), faqFollowups()); return; }

    handleSearchTurn(query);
  }

  var busy = false;
  function runTurn(rawQuery) {
    if (!rawQuery || !rawQuery.trim() || busy) return;
    var query = rawQuery.trim();
    busy = true;

    if (els.hero) els.hero.style.display = 'none';
    els.prompts.hidden = true;
    els.form.hidden = true;
    root.classList.add('fr-cc--chat-active');
    els.composer.hidden = false;

    appendUserTurn(query);
    var typingRow = appendTypingIndicator();

    var url = new URL(window.location.href);
    url.searchParams.set('q', query);
    window.history.replaceState({}, '', url.toString());

    setTimeout(function () {
      typingRow.remove();
      handleTurn(query);
      busy = false;
      if (!els.composer.hidden) els.composerInput.focus();
    }, 420 + Math.random() * 260);
  }

  // ---------- reset ----------

  function reset() {
    resetContext();
    els.input.value = '';
    els.composerInput.value = '';
    els.thread.hidden = true;
    els.thread.innerHTML = '';
    els.results.hidden = true;
    els.empty.hidden = true;
    els.prompts.hidden = false;
    els.form.hidden = false;
    els.composer.hidden = true;
    root.classList.remove('fr-cc--chat-active');
    if (els.hero) els.hero.style.display = '';
    var url = new URL(window.location.href);
    url.searchParams.delete('q');
    window.history.replaceState({}, '', url.toString());
    els.input.focus();
  }

  // ---------- wiring ----------

  els.form.addEventListener('submit', function (e) {
    e.preventDefault();
    runTurn(els.input.value);
  });
  els.composerForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var v = els.composerInput.value;
    els.composerInput.value = '';
    runTurn(v);
  });
  els.clear.addEventListener('click', reset);

  root.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('.fr-cc-chip[data-q]');
    if (chip) {
      var q = chip.getAttribute('data-q');
      els.input.value = q;
      runTurn(q);
      return;
    }
    var addBtn = e.target.closest && e.target.closest('.fr-cc-add');
    if (addBtn) { e.preventDefault(); addToCart(addBtn); }
  });

  // Auto-run if the page loaded with ?q= already set (shared/bookmarked link, or a
  // no-JS form submit that reloaded this same page with the term in the URL).
  var initialQ = new URL(window.location.href).searchParams.get('q');
  if (initialQ) {
    els.input.value = initialQ;
    runTurn(initialQ);
  }
})();
